import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { connectChatSocket, disconnectChatSocket, getChatSocket } from './chatSocket';
import {
  fetchConversations,
  fetchMessages,
  startConversation,
  sendMessage as apiSendMessage,
  markConversationRead,
  editMessage as apiEditMessage,
  deleteMessage as apiDeleteMessage,
  searchChatUsers,
} from '../api/chatApi';

const ChatContext = createContext(null);

const initialState = {
  conversations: [],
  conversationsLoaded: false,
  activeConversationId: null,
  messagesByConversation: {},
  presenceByUserId: {},
  typingByConversation: {},
  drawerOpen: false,
  searchResults: [],
};

const upsertConversation = (conversations, updated) => {
  const others = conversations.filter((c) => String(c.id) !== String(updated.id));
  return [updated, ...others].sort(
    (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0),
  );
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.conversations, conversationsLoaded: true };
    case 'UPSERT_CONVERSATION':
      return { ...state, conversations: upsertConversation(state.conversations, action.conversation) };
    case 'SET_ACTIVE':
      return { ...state, activeConversationId: action.conversationId };
    case 'SET_DRAWER_OPEN':
      return { ...state, drawerOpen: action.open };
    case 'SET_MESSAGES':
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [action.conversationId]: action.messages,
        },
      };
    case 'ADD_OR_UPDATE_MESSAGE': {
      const message = action.message;
      const convId = String(message.conversationId);
      const existing = state.messagesByConversation[convId] || [];
      const idx = existing.findIndex((m) => String(m.id) === String(message.id));
      const next = idx === -1 ? [...existing, message] : existing.map((m, i) => (i === idx ? message : m));
      return {
        ...state,
        messagesByConversation: { ...state.messagesByConversation, [convId]: next },
      };
    }
    case 'MARK_MESSAGES_READ': {
      const convId = String(action.conversationId);
      const existing = state.messagesByConversation[convId] || [];
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [convId]: existing.map((m) =>
            String(m.sender) === String(action.senderId) ? { ...m, status: 'read' } : m,
          ),
        },
      };
    }
    case 'MARK_MESSAGES_DELIVERED': {
      const convId = String(action.conversationId);
      const idsSet = new Set(action.messageIds.map(String));
      const existing = state.messagesByConversation[convId] || [];
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          // Never downgrade a message the UI already knows was read.
          [convId]: existing.map((m) =>
            idsSet.has(String(m.id)) && m.status !== 'read' ? { ...m, status: 'delivered' } : m,
          ),
        },
      };
    }
    case 'RESET_UNREAD': {
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          String(c.id) === String(action.conversationId) ? { ...c, unreadCount: 0 } : c,
        ),
      };
    }
    case 'SET_TYPING': {
      return {
        ...state,
        typingByConversation: {
          ...state.typingByConversation,
          [String(action.conversationId)]: action.typing ? action.userId : null,
        },
      };
    }
    case 'SET_PRESENCE':
      return {
        ...state,
        presenceByUserId: {
          ...state.presenceByUserId,
          [String(action.userId)]: { online: action.online, lastSeenAt: action.lastSeenAt },
        },
      };
    case 'SET_SEARCH_RESULTS':
      return { ...state, searchResults: action.results };
    default:
      return state;
  }
};

export const ChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const isLoggedIn = useSelector((s) => s.user.isLoggedIn);
  const currentUserId = useSelector((s) => s.user.userInfo?._id);
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingOpenChat = useRef(searchParams.get('openChat'));

  useEffect(() => {
    if (!isLoggedIn) {
      disconnectChatSocket();
      return undefined;
    }
    const socket = connectChatSocket();
    if (!socket) return undefined;

    const onMessage = (message) => {
      dispatch({ type: 'ADD_OR_UPDATE_MESSAGE', message });
      const isMine = String(message.sender) === String(currentUserId);
      const isActiveConversation = String(message.conversationId) === String(state.activeConversationId);
      dispatch({
        type: 'UPSERT_CONVERSATION',
        conversation: {
          id: message.conversationId,
          lastMessage: message.text,
          lastMessageAt: message.createdAt,
          unreadCount:
            !isMine && !isActiveConversation
              ? (state.conversations.find((c) => String(c.id) === String(message.conversationId))
                  ?.unreadCount || 0) + 1
              : 0,
          partner: state.conversations.find((c) => String(c.id) === String(message.conversationId))
            ?.partner,
        },
      });
      // The thread is already open — tell the server too, so a reload
      // doesn't bring back an unread count the user already saw live.
      if (!isMine && isActiveConversation) {
        markConversationRead(message.conversationId).catch(() => {});
      }
    };
    const onEdited = (message) => dispatch({ type: 'ADD_OR_UPDATE_MESSAGE', message });
    const onDeleted = (message) => dispatch({ type: 'ADD_OR_UPDATE_MESSAGE', message });
    const onDelivered = ({ conversationId, messageIds }) =>
      dispatch({ type: 'MARK_MESSAGES_DELIVERED', conversationId, messageIds });
    const onRead = ({ conversationId, readerId }) => {
      dispatch({ type: 'MARK_MESSAGES_READ', conversationId, senderId: currentUserId });
      if (String(readerId) === String(currentUserId)) {
        dispatch({ type: 'RESET_UNREAD', conversationId });
      }
    };
    const onTyping = ({ conversationId, userId }) =>
      dispatch({ type: 'SET_TYPING', conversationId, userId, typing: true });
    const onTypingStop = ({ conversationId, userId }) =>
      dispatch({ type: 'SET_TYPING', conversationId, userId, typing: false });
    const onPresence = ({ userId, online, lastSeenAt }) =>
      dispatch({ type: 'SET_PRESENCE', userId, online, lastSeenAt });
    const onProblemSolved = ({ name, username, problemTitle }) => {
      toast.info(`${name || username} just solved "${problemTitle}"!`);
    };

    socket.on('chat:message', onMessage);
    socket.on('chat:message_edited', onEdited);
    socket.on('chat:message_deleted', onDeleted);
    socket.on('chat:delivered', onDelivered);
    socket.on('chat:read', onRead);
    socket.on('chat:typing', onTyping);
    socket.on('chat:typing_stop', onTypingStop);
    socket.on('presence:update', onPresence);
    socket.on('problem:solved', onProblemSolved);

    return () => {
      socket.off('chat:message', onMessage);
      socket.off('chat:message_edited', onEdited);
      socket.off('chat:message_deleted', onDeleted);
      socket.off('chat:delivered', onDelivered);
      socket.off('chat:read', onRead);
      socket.off('chat:typing', onTyping);
      socket.off('chat:typing_stop', onTypingStop);
      socket.off('presence:update', onPresence);
      socket.off('problem:solved', onProblemSolved);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, currentUserId, state.activeConversationId, state.conversations]);

  const refreshConversations = useCallback(async () => {
    if (!isLoggedIn) return;
    const conversations = await fetchConversations();
    dispatch({ type: 'SET_CONVERSATIONS', conversations });
  }, [isLoggedIn]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  const loadMessages = useCallback(async (conversationId) => {
    const messages = await fetchMessages(conversationId);
    dispatch({ type: 'SET_MESSAGES', conversationId, messages });
  }, []);

  const selectConversation = useCallback(
    async (conversationId) => {
      dispatch({ type: 'SET_ACTIVE', conversationId });
      await loadMessages(conversationId);
      await markConversationRead(conversationId);
      dispatch({ type: 'RESET_UNREAD', conversationId });
    },
    [loadMessages],
  );

  const openDrawer = useCallback(() => dispatch({ type: 'SET_DRAWER_OPEN', open: true }), []);
  const closeDrawer = useCallback(() => dispatch({ type: 'SET_DRAWER_OPEN', open: false }), []);
  const clearActiveConversation = useCallback(
    () => dispatch({ type: 'SET_ACTIVE', conversationId: null }),
    [],
  );

  const openConversationWithUser = useCallback(
    async (userId) => {
      const conversation = await startConversation(userId);
      dispatch({
        type: 'UPSERT_CONVERSATION',
        conversation: {
          id: conversation._id,
          partner: state.conversations.find((c) => String(c.id) === String(conversation._id))
            ?.partner,
          lastMessage: conversation.lastMessage,
          lastMessageAt: conversation.lastMessageAt,
          unreadCount: 0,
        },
      });
      openDrawer();
      await selectConversation(conversation._id);
      await refreshConversations();
      return conversation._id;
    },
    [openDrawer, selectConversation, refreshConversations, state.conversations],
  );

  // One-shot: an unread-message reminder email links back here with
  // ?openChat=<conversationId> — open straight to that thread, then strip
  // the param so a refresh doesn't keep re-triggering it.
  useEffect(() => {
    if (!isLoggedIn || !state.conversationsLoaded || !pendingOpenChat.current) return;
    const conversationId = pendingOpenChat.current;
    pendingOpenChat.current = null;
    openDrawer();
    selectConversation(conversationId);
    const next = new URLSearchParams(searchParams);
    next.delete('openChat');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, state.conversationsLoaded]);

  const sendMessage = useCallback(
    async (conversationId, text) => {
      const message = await apiSendMessage(conversationId, text);
      dispatch({ type: 'ADD_OR_UPDATE_MESSAGE', message });
      dispatch({
        type: 'UPSERT_CONVERSATION',
        conversation: {
          id: conversationId,
          lastMessage: message.text,
          lastMessageAt: message.createdAt,
          unreadCount: 0,
          partner: state.conversations.find((c) => String(c.id) === String(conversationId))?.partner,
        },
      });
      return message;
    },
    [state.conversations],
  );

  const editMessage = useCallback(async (messageId, text) => {
    const message = await apiEditMessage(messageId, text);
    dispatch({ type: 'ADD_OR_UPDATE_MESSAGE', message });
    return message;
  }, []);

  const deleteMessage = useCallback(async (messageId) => {
    const message = await apiDeleteMessage(messageId);
    dispatch({ type: 'ADD_OR_UPDATE_MESSAGE', message });
    return message;
  }, []);

  const notifyTyping = useCallback((conversationId, recipientId) => {
    getChatSocket()?.emit('chat:typing', { conversationId, recipientId });
  }, []);

  const notifyTypingStop = useCallback((conversationId, recipientId) => {
    getChatSocket()?.emit('chat:typing_stop', { conversationId, recipientId });
  }, []);

  const searchUsers = useCallback(async (query) => {
    if (!query?.trim()) {
      dispatch({ type: 'SET_SEARCH_RESULTS', results: [] });
      return;
    }
    const results = await searchChatUsers(query);
    dispatch({ type: 'SET_SEARCH_RESULTS', results });
  }, []);

  const unreadTotal = useMemo(
    () => state.conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    [state.conversations],
  );

  const value = useMemo(
    () => ({
      ...state,
      currentUserId,
      unreadTotal,
      openDrawer,
      closeDrawer,
      selectConversation,
      clearActiveConversation,
      openConversationWithUser,
      sendMessage,
      editMessage,
      deleteMessage,
      notifyTyping,
      notifyTypingStop,
      searchUsers,
    }),
    [
      state,
      currentUserId,
      unreadTotal,
      openDrawer,
      closeDrawer,
      selectConversation,
      clearActiveConversation,
      openConversationWithUser,
      sendMessage,
      editMessage,
      deleteMessage,
      notifyTyping,
      notifyTypingStop,
      searchUsers,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within a ChatProvider');
  return ctx;
};
